"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import { useLightbox } from "./lightbox/LightboxContext";

const FAC_IMAGES = [
  "/assets/pool.jpg",
  "/assets/gym.jpg",
  "/assets/lounge-bar.jpg",
  "/assets/dining-lounge.jpg",
  "/assets/lounge.jpg",
  "/assets/minimart-cafe.jpg",
  "/assets/entrance.jpg",
  "/assets/pool-aerial.jpg",
];

const BENTO = [
  { src: "/assets/pool-aerial.jpg", altIdx: 2, big: true },
  { src: "/assets/lounge-bar.jpg", altIdx: 4, big: false },
  { src: "/assets/gym.jpg", altIdx: 7, big: false },
  { src: "/assets/dining-lounge.jpg", altIdx: 5, big: false },
  { src: "/assets/lounge.jpg", altIdx: 6, big: false },
  { src: "/assets/minimart-cafe.jpg", altIdx: 8, big: false },
];

export function Amenities() {
  const t = useTranslations();
  const { open } = useLightbox();
  const swiperRef = useRef<SwiperClass | null>(null);
  const stripRef = useRef<HTMLUListElement | null>(null);
  const [index, setIndex] = useState(0);

  const amenities = t.raw("amenities") as { name: string; note: string }[];
  const galAlts = t.raw("galAlts") as string[];
  const slides = FAC_IMAGES.map((src, i) => ({ src, ...amenities[i] }));

  const goTo = (i: number) => {
    swiperRef.current?.slideTo(i);
    setIndex(i);
  };

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || strip.scrollWidth <= strip.clientWidth + 4) return;
    const el = strip.querySelector<HTMLElement>(`[data-fac="${index}"]`);
    if (!el) return;
    strip.scrollTo({
      left: el.offsetLeft - (strip.clientWidth - el.offsetWidth) / 2,
      behavior: "smooth",
    });
  }, [index]);

  return (
    <section className="bg-ink text-white">
      <div className="mx-auto max-w-[1280px] px-[clamp(20px,4vw,40px)] py-[clamp(72px,9vw,120px)]">
        <div data-reveal className="mb-[clamp(32px,4vw,48px)] max-w-[56ch]">
          <p className="mb-[18px] text-xs uppercase tracking-[.2em] text-white/50">
            {t("amenKicker")}
          </p>
          <h2 className="text-[clamp(28px,3.4vw,44px)] leading-[1.12] text-white">
            {t("amenTitle")}
          </h2>
        </div>

        <div className="grid items-start gap-[clamp(28px,4vw,56px)] min-[900px]:grid-cols-[minmax(300px,0.9fr)_minmax(0,1.1fr)]">
          <ul
            ref={stripRef}
            data-reveal
            className="sop-scroll-strip m-0 flex list-none gap-2 overflow-x-auto pb-1.5 min-[900px]:block min-[900px]:gap-0 min-[900px]:overflow-visible min-[900px]:border-t min-[900px]:border-white/[.16] min-[900px]:pb-0"
          >
            {slides.map((a, i) => {
              const active = index === i;
              return (
                <li
                  key={a.name}
                  data-fac={i}
                  className="flex-none rounded-[3px] border border-white/[.18] min-[900px]:flex-initial min-[900px]:rounded-none min-[900px]:border-0 min-[900px]:border-b min-[900px]:border-white/[.16]"
                >
                  <button
                    type="button"
                    onClick={() => goTo(i)}
                    aria-pressed={active}
                    className="flex w-full items-baseline gap-4 whitespace-nowrap px-4 py-3 text-left transition-colors min-[900px]:whitespace-normal min-[900px]:px-1 min-[900px]:py-[17px]"
                    style={{ color: active ? "#fff" : "rgba(255,255,255,.62)" }}
                  >
                    <span
                      className="flex-none text-[11.5px] tracking-[.08em] transition-colors"
                      style={{
                        color: active ? "#7FB3BF" : "rgba(255,255,255,.35)",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="min-w-0">
                      <span className="font-display block text-[16.5px] font-medium leading-[1.35]">
                        {a.name}
                      </span>
                      <span
                        className="mt-1.5 hidden text-[13.5px] leading-[1.55] font-light transition-colors min-[900px]:block"
                        style={{
                          color: active ? "rgba(255,255,255,.72)" : "rgba(255,255,255,.4)",
                        }}
                      >
                        {a.note}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div data-reveal className="flex h-full min-w-0 flex-col gap-3.5">
            <div className="relative min-h-[300px] flex-1 overflow-hidden border border-white/[.14] bg-ink-2">
              <Swiper
                onSwiper={(s) => (swiperRef.current = s)}
                onSlideChange={(s) => setIndex(s.activeIndex)}
                className="h-full w-full"
              >
                {slides.map((slide) => (
                  <SwiperSlide key={slide.src}>
                    <div className="relative h-full w-full">
                      <Image
                        src={slide.src}
                        alt={slide.name}
                        fill
                        sizes="(min-width: 900px) 55vw, 100vw"
                        className="object-cover"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              <button
                type="button"
                onClick={() =>
                  open(
                    slides.map((s) => ({ src: s.src, caption: s.name })),
                    index,
                  )
                }
                aria-label="Fullscreen"
                className="absolute right-3.5 top-3.5 z-10 flex h-[38px] w-[38px] items-center justify-center rounded-[3px] border-0 bg-[rgba(15,17,19,.72)] text-[20px] leading-none text-white transition-colors hover:bg-[#0F1113]"
              >
                +
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => swiperRef.current?.slidePrev()}
                aria-label={t("prev")}
                className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[3px] border border-white/[.24] bg-none text-[16px] text-white transition-colors hover:bg-white hover:text-ink"
              >
                ←
              </button>
              <button
                type="button"
                onClick={() => swiperRef.current?.slideNext()}
                aria-label={t("next")}
                className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[3px] border border-white/[.24] bg-none text-[16px] text-white transition-colors hover:bg-white hover:text-ink"
              >
                →
              </button>
              <p className="ml-auto min-w-0 text-right text-[13px] text-white/60">
                {slides[index]?.name} · {index + 1} / {slides.length}
              </p>
            </div>
          </div>
        </div>

        <div
          data-reveal
          className="mt-[clamp(32px,4vw,48px)] grid grid-cols-1 gap-2.5 min-[900px]:grid-cols-6 min-[900px]:auto-rows-[clamp(170px,19vw,250px)] min-[900px]:gap-3.5"
        >
          {BENTO.map((b) => (
            <div
              key={b.src}
              className={
                "relative h-[clamp(220px,56vw,340px)] overflow-hidden rounded bg-ink-2 min-[900px]:h-auto " +
                (b.big
                  ? "min-[900px]:col-span-4 min-[900px]:row-span-2"
                  : "min-[900px]:col-span-2 min-[900px]:row-span-1")
              }
            >
              <Image
                src={b.src}
                alt={galAlts[b.altIdx]}
                fill
                sizes="(min-width: 900px) 33vw, 100vw"
                className="object-cover"
              />
              <p className="pointer-events-none absolute inset-x-4 bottom-4 m-0 text-[13.5px] font-medium leading-[1.4] text-white [text-shadow:0_1px_14px_rgba(15,17,19,.85)]">
                {galAlts[b.altIdx]}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
